import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const GATEWAY_URL = "https://connector-gateway.lovable.dev/twilio";

// Normalize any Indian/E.164 input to strict E.164: +91XXXXXXXXXX
function normalizePhone(raw: string) {
  let digits = (raw || "").replace(/\D/g, "");
  if (digits.length > 10 && digits.startsWith("0")) digits = digits.replace(/^0+/, "");
  if (digits.length === 10) digits = `91${digits}`;
  return `+${digits}`;
}

function isValidIndianE164(phone: string) {
  return /^\+91[6-9]\d{9}$/.test(phone);
}

function phoneToSyntheticEmail(phone: string) {
  // Deterministic synthetic email so the same phone maps to the same auth user.
  const digits = normalizePhone(phone).replace(/\D/g, "");
  return `p${digits}@phone.brothersclub.app`;
}

function projectRef(url?: string) {
  const m = /https?:\/\/([a-z0-9]+)\.supabase\.co/i.exec(url ?? "");
  return m?.[1] ?? "unknown";
}

type VerifyLog = {
  stage: string;
  success: boolean;
  safe_code: string | null;
  twilio_status: string | null;
  supabase_status: string | null;
  has_user_id: boolean;
  has_token_hash: boolean;
};

function logVerify(entry: VerifyLog) {
  // Only safe fields: never OTP, phone, email, tokens, or secrets.
  console.info("[verifyPhoneOtp]", JSON.stringify(entry));
}


/** Non-reversible fingerprint of (phone, code); never stores the OTP itself. */
async function approvalCodeHash(phoneE164: string, code: string): Promise<string> {
  const bytes = new TextEncoder().encode(`${phoneE164}:${code}`);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}


async function twilioVerifyRequest(path: string, body: Record<string, string>) {
  const accountSid = process.env.TWILIO_ACCOUNT_SID;
  const authToken = process.env.TWILIO_AUTH_TOKEN;
  const serviceSid = process.env.TWILIO_VERIFY_SERVICE_SID;
  if (!accountSid || !authToken || !serviceSid) {
    throw new Error("Twilio credentials are not configured.");
  }
  // Twilio Verify uses its own base path; call it directly with Basic Auth.
  const url = `https://verify.twilio.com/v2/Services/${serviceSid}${path}`;
  const auth = Buffer.from(`${accountSid}:${authToken}`).toString("base64");
  const res = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Basic ${auth}`,
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body: new URLSearchParams(body).toString(),
  });
  const json = (await res.json()) as any;
  if (!res.ok) {
    const msg = json?.message || `Twilio error ${res.status}`;
    throw new Error(msg);
  }
  return json;
}

function twilioConfigured() {
  return !!(
    process.env.TWILIO_ACCOUNT_SID &&
    process.env.TWILIO_AUTH_TOKEN &&
    process.env.TWILIO_VERIFY_SERVICE_SID
  );
}

export const sendPhoneOtp = createServerFn({ method: "POST" })
  .inputValidator((d) => z.object({ phone: z.string().min(8) }).parse(d))
  .handler(async ({ data }) => {
    if (!twilioConfigured()) {
      // Demo/test mode: no SMS sent. Client can use code 123456.
      return { ok: true, demo: true };
    }
    try {
      await twilioVerifyRequest("/Verifications", {
        To: data.phone,
        Channel: "sms",
      });
    } catch (e: any) {
      throw new Error(e?.message ?? "Failed to send OTP. Please try again.");
    }
    return { ok: true, demo: false };
  });

export type VerifyPhoneOtpFailureCode =
  | "invalid_phone"
  | "twilio_check_failed"
  | "invalid_code"
  | "expired_code"
  | "auth_user_lookup_failed"
  | "auth_user_create_failed"
  | "magic_link_generation_failed"
  | "missing_token_hash"
  | "unexpected_server_error";

export type VerifyPhoneOtpResult =
  | { ok: true; email: string; token_hash: string; user_id: string }
  | { ok: false; code: VerifyPhoneOtpFailureCode; message: string };

// Fast path: resolve the account via public.profiles (indexed lookup on the
// synthetic email / phone). Falls back to a bounded auth-user scan. The old
// unbounded 200-page listUsers scan could exceed the edge response budget and
// return an empty body to the client ("empty verify response").
async function findAuthUser(
  admin: any,
  email: string,
  phoneE164: string,
): Promise<{ id: string; email?: string } | null> {
  const phoneDigits = phoneE164.replace(/\D/g, "");
  try {
    const { data: profile } = await admin
      .from("profiles")
      .select("id")
      .or(`email.eq.${email},phone.eq.${phoneDigits}`)
      .limit(1)
      .maybeSingle();
    if (profile?.id) {
      const { data: byId } = await admin.auth.admin.getUserById(profile.id);
      if (byId?.user) return byId.user as { id: string; email?: string };
    }
  } catch {
    // fall through to the scan below
  }

  const perPage = 1000;
  // Bounded scan with a wall-clock budget: the handler must always return a
  // body. Running past the edge response budget is what produced empty verify
  // responses on the Android shell.
  const scanDeadline = Date.now() + 4000;
  for (let page = 1; page <= 10; page++) {
    if (Date.now() > scanDeadline) return null;
    const { data, error } = await admin.auth.admin.listUsers({ page, perPage });
    if (error) throw error;
    const users: any[] = data?.users ?? [];
    const hit = users.find(
      (u) =>
        (u.email ?? "").toLowerCase() === email.toLowerCase() ||
        (u.phone ? normalizePhone(u.phone) : "") === phoneE164,
    );
    if (hit) return hit;
    if (users.length < perPage) return null;
  }
  return null;
}



export const verifyPhoneOtp = createServerFn({ method: "POST" })
  .inputValidator((d) =>
    z.object({ phone: z.string().min(8), code: z.string().min(4) }).parse(d),
  )
  .handler(async ({ data }): Promise<VerifyPhoneOtpResult> => {
    // Never throw: thrown errors do not survive the cross-origin RPC boundary
    // in the Android bundle. Always return a typed, serializable result.
    let twilioStatus: string | null = null;

    const fail = (
      stage: string,
      code: VerifyPhoneOtpFailureCode,
      message: string,
      supabaseStatus: string | null = null,
      hasUserId = false,
      hasTokenHash = false,
    ): VerifyPhoneOtpResult => {
      logVerify({
        stage,
        success: false,
        safe_code: code,
        twilio_status: twilioStatus,
        supabase_status: supabaseStatus,
        has_user_id: hasUserId,
        has_token_hash: hasTokenHash,
      });
      return { ok: false, code, message };
    };

    try {
      const phoneE164 = normalizePhone(data.phone);
      if (!isValidIndianE164(phoneE164)) {
        return fail(
          "normalize_phone",
          "invalid_phone",
          "That mobile number is not a valid Indian mobile number.",
        );
      }
      const email = phoneToSyntheticEmail(phoneE164);

      // Admin client is needed both for the approval ledger and the user work.
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

      // Records that this phone passed Twilio verification, so a retry after a
      // lost/empty HTTP response can still complete sign-in (Twilio consumes
      // the verification on first approval and 404s afterwards).
      const APPROVAL_TTL_MS = 10 * 60 * 1000;
      const rememberApproval = async () => {
        try {
          await supabaseAdmin.from("otp_approvals").upsert({
            phone: phoneE164,
            approved_at: new Date().toISOString(),
            // Binds the resume path to the exact code the user entered, so a
            // phone number alone can never mint a session.
            code_hash: await approvalCodeHash(phoneE164, data.code),
            consumed_at: null,
          });
        } catch (e: any) {
          console.error("[verifyPhoneOtp] approval_persist_failed:", e?.message ?? e);
        }
      };
      const hasRecentApproval = async () => {
        try {
          const { data: row } = await supabaseAdmin
            .from("otp_approvals")
            .select("approved_at, code_hash")
            .eq("phone", phoneE164)
            .maybeSingle();
          if (!row?.approved_at) return false;
          if (Date.now() - new Date(row.approved_at).getTime() >= APPROVAL_TTL_MS) return false;
          const expected = await approvalCodeHash(phoneE164, data.code);
          return !row.code_hash || row.code_hash === expected;
        } catch {
          return false;
        }
      };


      // ---- Stage 1: Twilio verification check ----
      if (!twilioConfigured()) {
        if (data.code !== "123456") {
          twilioStatus = "demo_mode";
          return fail("twilio_check", "invalid_code", "Invalid or expired code.");
        }
        twilioStatus = "demo_mode";
      } else {
        let check: any;
        try {
          check = await twilioVerifyRequest("/VerificationCheck", {
            To: phoneE164,
            Code: data.code,
          });
        } catch (e: any) {
          const msg = String(e?.message ?? "");
          twilioStatus = "request_failed";
          // Twilio returns 404 for verifications already expired/consumed.
          if (/404|not found|expired/i.test(msg)) {
            if (await hasRecentApproval()) {
              // Same code already approved moments ago; the client simply never
              // received the response. Resume instead of failing.
              twilioStatus = "resumed_from_approval";
            } else {
              return fail(
                "twilio_check",
                "expired_code",
                "This OTP has expired or was already used. Please request a new one.",
              );
            }
          } else {
            console.error("[verifyPhoneOtp] twilio_check_failed:", msg);
            return fail(
              "twilio_check",
              "twilio_check_failed",
              "We could not verify the OTP with the SMS provider. Please try again.",
            );
          }
        }
        if (twilioStatus !== "resumed_from_approval") {
          twilioStatus = String(check?.status ?? "unknown");
          if (check?.status !== "approved") {
            return fail("twilio_check", "invalid_code", "Invalid or expired code.");
          }
          await rememberApproval();
        }
      }



      // Project reference sanity check (no keys logged).
      const serverRef = projectRef(process.env.SUPABASE_URL);
      console.info(
        "[verifyPhoneOtp] supabase_project_ref=",
        serverRef,
        "public_ref=",
        projectRef(process.env.VITE_SUPABASE_URL ?? process.env.SUPABASE_URL),
      );

      // ---- Stage 2: find existing auth user ----
      let user: { id: string; email?: string } | null = null;
      try {
        user = await findAuthUser(supabaseAdmin, email, phoneE164);
      } catch (e: any) {
        console.error("[verifyPhoneOtp] auth_user_lookup_failed:", e?.message ?? e);
        return fail(
          "auth_user_lookup",
          "auth_user_lookup_failed",
          "OTP was approved, but the account could not be located.",
          e?.status ? String(e.status) : null,
        );
      }

      // ---- Stage 3: create user only when truly absent ----
      if (!user) {
        const created = await supabaseAdmin.auth.admin.createUser({
          email,
          phone: phoneE164,
          email_confirm: true,
          phone_confirm: true,
          user_metadata: { name: "Player", phone: phoneE164 },
        });
        if (created.data?.user) {
          user = created.data.user as any;
        } else {
          const msg = created.error?.message ?? "unknown";
          console.error("[verifyPhoneOtp] create_user error:", msg);
          // "already registered" -> re-scan and reuse the existing account.
          try {
            user = await findAuthUser(supabaseAdmin, email, phoneE164);
          } catch {
            user = null;
          }
          if (!user) {
            return fail(
              "auth_user_create",
              "auth_user_create_failed",
              "OTP was approved, but your account could not be created. Please try again.",
              created.error?.status ? String(created.error.status) : null,
            );
          }
        }
      }

      // ---- Stage 4: validate the resolved user ----
      if (!user?.id || (user.email ?? "").toLowerCase() !== email.toLowerCase()) {
        return fail(
          "auth_user_validate",
          "auth_user_lookup_failed",
          "OTP was approved, but the account could not be located.",
          null,
          !!user?.id,
        );
      }

      // ---- Stage 5: magic link ----
      const link = await supabaseAdmin.auth.admin.generateLink({
        type: "magiclink",
        email,
      });
      if (link.error) {
        const msg = link.error.message ?? "unknown";
        console.error(
          "[verifyPhoneOtp] generate_link error:",
          msg,
          "status=",
          link.error.status ?? "none",
        );
        const disabled = /disabled|not enabled|signups? not allowed|provider/i.test(msg);
        return fail(
          "generate_link",
          "magic_link_generation_failed",
          disabled
            ? "OTP was approved, but email/magic-link sign-in is disabled on the backend. An admin must enable the email provider."
            : "OTP was approved, but the sign-in token could not be created.",
          link.error.status ? String(link.error.status) : null,
          true,
        );
      }

      // ---- Stage 6: token hash ----
      const tokenHash = link.data?.properties?.hashed_token;
      if (!tokenHash) {
        return fail(
          "token_hash",
          "missing_token_hash",
          "The sign-in token response did not contain a token hash.",
          null,
          true,
          false,
        );
      }

      logVerify({
        stage: "complete",
        success: true,
        safe_code: null,
        twilio_status: twilioStatus,
        supabase_status: "200",
        has_user_id: true,
        has_token_hash: true,
      });

      return { ok: true, email, token_hash: tokenHash, user_id: user.id };
    } catch (e: any) {
      console.error("[verifyPhoneOtp] unexpected failure:", e?.message ?? e);
      return fail(
        "unexpected",
        "unexpected_server_error",
        "OTP verification failed unexpectedly. Please request a new OTP and try again.",
      );
    }
  });

// ---------------------------------------------------------------------------
// Approval-resume (AUTH-177324).
//
// When the verify RPC completed server-side but its response body never
// reached the Android shell, the OTP is already consumed at Twilio and a plain
// retry can never succeed. This lookup-only call re-issues a sign-in token
// from the recorded approval. It touches no provider and performs no user
// scan, so it stays comfortably inside the edge response budget.
// ---------------------------------------------------------------------------
export const resumePhoneLogin = createServerFn({ method: "POST" })
  .inputValidator((d) =>
    z.object({ phone: z.string().min(8), code: z.string().min(4) }).parse(d),
  )
  .handler(async ({ data }): Promise<VerifyPhoneOtpResult> => {
    const APPROVAL_TTL_MS = 10 * 60 * 1000;
    try {
      const phoneE164 = normalizePhone(data.phone);
      if (!isValidIndianE164(phoneE164)) {
        return {
          ok: false,
          code: "invalid_phone",
          message: "That mobile number is not a valid Indian mobile number.",
        };
      }
      const email = phoneToSyntheticEmail(phoneE164);
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

      const { data: approval } = await supabaseAdmin
        .from("otp_approvals")
        .select("approved_at, code_hash, consumed_at")
        .eq("phone", phoneE164)
        .maybeSingle();
      const approvedAt = approval?.approved_at ? new Date(approval.approved_at).getTime() : 0;
      const codeMatches =
        !!approval?.code_hash &&
        approval.code_hash === (await approvalCodeHash(phoneE164, data.code));
      if (!approvedAt || Date.now() - approvedAt > APPROVAL_TTL_MS || !codeMatches || approval?.consumed_at) {

        logVerify({
          stage: "resume_no_approval",
          success: false,
          safe_code: "expired_code",
          twilio_status: null,
          supabase_status: null,
          has_user_id: false,
          has_token_hash: false,
        });
        return {
          ok: false,
          code: "expired_code",
          message: "This OTP has expired or was already used. Please request a new one.",
        };
      }

      // Fast path only — no listUsers scan on the resume path.
      let user: { id: string; email?: string } | null = null;
      try {
        const { data: profile } = await supabaseAdmin
          .from("profiles")
          .select("id")
          .or(`email.eq.${email},phone.eq.${phoneE164.replace(/\D/g, "")}`)
          .limit(1)
          .maybeSingle();
        if (profile?.id) {
          const { data: byId } = await supabaseAdmin.auth.admin.getUserById(profile.id);
          if (byId?.user) user = byId.user as { id: string; email?: string };
        }
      } catch (e: any) {
        console.error("[resumePhoneLogin] lookup_failed:", e?.message ?? e);
      }

      if (!user?.id) {
        return {
          ok: false,
          code: "auth_user_lookup_failed",
          message: "OTP was approved, but the account could not be located.",
        };
      }

      const link = await supabaseAdmin.auth.admin.generateLink({
        type: "magiclink",
        email: user.email ?? email,
      });
      const tokenHash = link.data?.properties?.hashed_token;
      if (link.error || !tokenHash) {
        return {
          ok: false,
          code: link.error ? "magic_link_generation_failed" : "missing_token_hash",
          message: "OTP was approved, but the sign-in token could not be created.",
        };
      }

      // Single use: the approval cannot mint a second session.
      try {
        await supabaseAdmin
          .from("otp_approvals")
          .update({ consumed_at: new Date().toISOString() })
          .eq("phone", phoneE164);
      } catch (e: any) {
        console.error("[resumePhoneLogin] approval_consume_failed:", e?.message ?? e);
      }


      logVerify({
        stage: "resume_complete",
        success: true,
        safe_code: null,
        twilio_status: "resumed_from_approval",
        supabase_status: "200",
        has_user_id: true,
        has_token_hash: true,
      });
      return { ok: true, email: user.email ?? email, token_hash: tokenHash, user_id: user.id };
    } catch (e: any) {
      console.error("[resumePhoneLogin] unexpected failure:", e?.message ?? e);
      return {
        ok: false,
        code: "unexpected_server_error",
        message: "Sign-in could not be completed. Please request a new OTP.",
      };
    }
  });
